<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import {
  Notivue,
  NotivueKeyboard,
  NotivueSwipe,
  useNotivue
} from 'notivue';
import type { NotivueItem } from 'notivue';

import AppNotificationItem from './AppNotificationItem.vue';
import type { AppNotificationRenderProps } from '@shared/notifications/appNotificationTypes';

const route = useRoute();
const { isStreamPaused } = useNotivue();

const hostClass = computed(() => ({
  'app-notifications': true,
  'app-notifications--fullscreen':
    route.meta.layoutVariant === 'kiosk' ||
    route.meta.layoutVariant === 'fullscreen'
}));

const isAppNotificationItem = (
  item: NotivueItem
): item is NotivueItem<AppNotificationRenderProps> =>
  typeof item.props?.title === 'string' &&
  typeof item.props?.message === 'string' &&
  typeof item.props?.tone === 'string' &&
  typeof item.props?.role === 'string' &&
  typeof item.props?.ariaLive === 'string' &&
  Array.isArray(item.props?.reasons) &&
  typeof item.props?.hiddenReasonCount === 'number' &&
  typeof item.props?.repeatCount === 'number' &&
  typeof item.props?.closable === 'boolean';

const isSwipeDisabled = (
  item: NotivueItem<AppNotificationRenderProps>
) =>
  item.type === 'promise' ||
  item.duration === Infinity ||
  item.props.tone === 'blocker';
</script>

<template>
  <NotivueKeyboard
    combo-key="n"
    leave-message="Вы вышли из списка уведомлений. Нажмите Control и N, чтобы вернуться."
    empty-message="Нет уведомлений"
    :render-announcement="false"
  >
    <template #default="{ containersTabIndex, elementsTabIndex }">
      <Notivue
        :class="hostClass"
        :containers-tab-index="containersTabIndex"
        list-aria-label="Уведомления"
      >
        <template #default="item">
          <template v-if="isAppNotificationItem(item)">
            <NotivueSwipe
              :item="item"
              touch-only
              :disabled="isSwipeDisabled(item)"
            >
              <AppNotificationItem
                :item="item"
                :elements-tab-index="elementsTabIndex"
                :stream-paused="isStreamPaused"
              />
            </NotivueSwipe>
          </template>
        </template>
      </Notivue>
    </template>
  </NotivueKeyboard>
</template>

<style>
.app-notifications {
  --nv-root-width: 100%;
  --nv-root-top: 6rem;
  --nv-root-right: 1rem;
  --nv-root-left: 1rem;
  --nv-gap: 0.75rem;
  --nv-z: 1100;
}

.app-notifications--fullscreen {
  --nv-root-top: 0.75rem;
}

.app-toast-enter {
  animation: app-toast-enter 260ms var(--motion-ease-decelerate) both;
}

.app-toast-leave,
.app-toast-clear-all {
  animation: app-toast-leave 200ms var(--motion-ease-accelerate) both;
}

@keyframes app-toast-enter {
  from {
    transform: translateX(calc(100% + var(--motion-distance-lg)));
  }

  to {
    transform: translateX(0);
  }
}

@keyframes app-toast-leave {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(calc(100% + var(--motion-distance-lg)));
  }
}

@media (max-width: 640px) {
  .app-notifications {
    --nv-root-width: 100%;
    --nv-root-top: 5.5rem;
    --nv-root-right: 0.75rem;
    --nv-root-left: 0.75rem;
    --nv-gap: 0.625rem;
  }

  .app-notifications--fullscreen {
    --nv-root-top: 0.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-toast-enter,
  .app-toast-leave,
  .app-toast-clear-all {
    animation-duration: 1ms;
  }
}
</style>
